import React, {
  FC,
  useState,
  useRef,
  useMemo,
  useCallback,
  RefObject,
} from 'react'

import shouldAcceptDrop, { DropzoneDragGroupIds } from './should-accept-drop'

/**
Note: `drag`, which fires continuously, is not included

 #### `dragend`
 A drag operation is being ended (by releasing a mouse button or hitting the escape key)

 #### `dragenter`
 A dragged element or text selection enters a valid drop target.

 #### `dragstart`
 The user starts dragging an element or text selection.

 #### `dragleave`
 A dragged element or text selection leaves a valid drop target.

 #### `dragover`
 An element or text selection is being dragged over a valid drop target (fired continuously every 350ms).

 #### `drop`
 An element is dropped on a valid drop target.

 */
type EventName =
  | 'dragend'
  | 'dragenter'
  | 'dragstart'
  | 'dragleave'
  | 'dragover'
  | 'drop'

export type DragEffectCallback = (
  event: Event,
  draggedElem: HTMLElement | undefined,
  dropzoneElem: HTMLElement | undefined,
  activeDragGroupIds: string[],
  matchingDragGroupIds?: DropzoneDragGroupIds,
) => void

export type DragAndDropListenerMapping = Partial<
  Record<EventName, EventListener | null>
>
type GetListenerMapping = (
  dragGroupIds: string[],
  dropzoneDragGroupIds: DropzoneDragGroupIds,
) => DragAndDropListenerMapping

type CallbackRegistry = Record<string, DragEffectCallback[]>

export type ToggleDragDrops = (
  dragGroupIds: string[],
  update?: 'enable' | 'disable' | 'toggle',
) => void

export type DragNDropContextValue = {
  // enabling/disabling dragging
  enabledDragGroupIds: string[]
  isDraggingEnabled: (dragGroupIds: string[]) => boolean
  toggleDragDrops: ToggleDragDrops

  // active drag groups
  currentlyDraggingDragGroupIds: string[]

  // active dragged element
  activelyDraggedElem: HTMLElement | null
  activelyDraggedElemRef: RefObject<HTMLElement | null>

  // entered dropzone element
  activelyEnteredElem: HTMLElement | null
  activelyEnteredElemRef: RefObject<HTMLElement | null>

  // event callback management
  registerCallback: (
    dragGroupId: string | string[],
    dragEffectCallback: DragEffectCallback,
  ) => DragEffectCallback
  deregisterCallback: (
    dragGroupId: string | string[],
    dragEffectCallback: DragEffectCallback,
  ) => void

  // mainly serves as a helper for useDragNDrop
  //  useDragNDrop uses this in conjunction with useDragNDropListeners
  //  to attach all the relevant drag & drop event listeners onto html elements
  getListenerMapping: GetListenerMapping
}
export const DragNDropStateContext = React.createContext<
  DragNDropContextValue | undefined
>(undefined)

/**
 * Include **DragNDropProvider** as an ancestor for the React subtree
 *  for which you want to enable the inclusion of draggable elements,
 *  dropzone for dragged elements, and any functionality for toggling
 *  whether the dragging should be enabled or not
 *
 * ## See also
 *
 * #### **DragGroupEffect**
 *
 *   The **DragGroupEffect** component may be used as a descendant of this component
 *    to register side-effects when drag & drop related events fire for drag groups
 *
 * #### **useDragNDrop**
 *
 *   The **useDragNDrop** React hook is the recommended mechanism for interacting with
 *    the contextual state information provided by **DragNDropProvider** and allows
 *    you to make elements draggable, dropzones for draggable elements, and more
 *
 */
const DragNDropProvider: FC<{}> = ({ children }) => {
  const callbackRegistryRef = useRef<CallbackRegistry>({})

  // Note: Ugly code ahead!
  //   Here we use a conjunction of useState, useRef, and useCallback
  //   so that we can maintain referentially-equal listener mappings
  //   even as the state changes (which is important since adding & removing
  //   listeners as they change is expensive) but still support updates
  //   for other use cases where it is actually important to react
  //   to each state change
  const [
    enabledDragGroupIds,
    _setEnabledDragGroupIds, // only use in setEnabledDragGroupIds
  ] = useState<string[]>([])
  const enabledDragGroupIdsRef = useRef<string[]>(enabledDragGroupIds)
  const setEnabledDragGroupIds = useCallback(
    (nextEnabledDragGroupIds: string[]) => {
      enabledDragGroupIdsRef.current = nextEnabledDragGroupIds
      _setEnabledDragGroupIds(nextEnabledDragGroupIds)
    },
    [],
  )

  const [
    activelyDraggedElem,
    _setActivelyDraggedElem, // only use in setActivelyDraggedElem
  ] = useState<HTMLElement | null>(null)
  const activelyDraggedElemRef = useRef<HTMLElement | null>(null)
  const setActivelyDraggedElem = useCallback(
    (nextActivelyDraggedElem: HTMLElement | null) => {
      activelyDraggedElemRef.current = nextActivelyDraggedElem
      _setActivelyDraggedElem(nextActivelyDraggedElem)
    },
    [],
  )

  const [
    activelyEnteredElem,
    _setActivelyEnteredElem, // only use in setActivelyEnteredElem
  ] = useState<HTMLElement | null>(null)
  const activelyEnteredElemRef = useRef<HTMLElement | null>(null)
  const setActivelyEnteredElem = useCallback(
    (nextActivelyEnteredElem: HTMLElement | null) => {
      activelyEnteredElemRef.current = nextActivelyEnteredElem
      _setActivelyEnteredElem(nextActivelyEnteredElem)
    },
    [],
  )

  const [
    currentlyDraggingDragGroupIds,
    _setCurrentlyDraggingDragGroupIds, // only use in setCurrentlyDraggingDragGroupIds
  ] = useState<string[]>([])
  const currentlyDraggingDragGroupIdsRef = useRef<string[]>(
    currentlyDraggingDragGroupIds,
  )
  const setCurrentlyDraggingDragGroupIds = useCallback(
    (nextCurrentlyDraggingDragGroupIds: string[]) => {
      currentlyDraggingDragGroupIdsRef.current =
        nextCurrentlyDraggingDragGroupIds
      _setCurrentlyDraggingDragGroupIds(nextCurrentlyDraggingDragGroupIds)
    },
    [],
  )

  /**
   * Allows setting/toggling whether a drag group is enabled for dragging
   *
   * @param dragGroupIds
   *
   * ids for groups for which to change the drag drop state (if applicable)
   *
   * @param update
   *
   *   If `'enable'`, enables dragging for elements associated with any of the relevant drag group ids
   *
   *   If `'disable'`, stops enabling dragging based on relevant drag group ids
   *
   *   If `undefined` or `'toggle'`, toggles whether dragging is enabled for each drag group id
   */
  const toggleDragDrops: ToggleDragDrops = useCallback(
    (
      dragGroupIds: string[],
      update: 'enable' | 'disable' | 'toggle' = 'toggle',
    ) => {
      const currentEnabledDragGroupIds = enabledDragGroupIdsRef.current
      for (const dragGroupId of dragGroupIds) {
        const isEnabled = currentEnabledDragGroupIds.includes(dragGroupId)
        const nextIsEnabled =
          update === 'toggle' ? !isEnabled : update === 'enable'
        if (nextIsEnabled !== isEnabled) {
          const nextEnabledDragGroupIds = nextIsEnabled
            ? currentEnabledDragGroupIds.concat(dragGroupId)
            : currentEnabledDragGroupIds.filter(
                (enabledDragGroupId) => enabledDragGroupId !== dragGroupId,
              )
          setEnabledDragGroupIds(nextEnabledDragGroupIds)
        }
      }
    },
    [setEnabledDragGroupIds],
  )

  /**
   * Specifies whether dragging is enabled for any of the drag groups specified
   *  (and consequently whether an element with such drag group ids may be dragged)
   *
   * @param dragGroupIds
   *
   * ids for drag groups an element is associated with
   */
  const isDraggingEnabled = useCallback(
    (dragGroupIds: string[]) =>
      dragGroupIds.some((dragGroupId) =>
        enabledDragGroupIds.includes(dragGroupId),
      ),
    [enabledDragGroupIds],
  )

  /**
   * Returns the array of drag group ids that are currently
   *  enabled and associated with the element being dragged (if any)
   */
  const getCurrentActivatedDropzoneDragGroupIds = useCallback(
    () =>
      currentlyDraggingDragGroupIdsRef.current.filter((groupId) =>
        enabledDragGroupIdsRef.current.includes(groupId),
      ),
    [],
  )

  /**
   * Specifies the drag group ids (or combinations) which allow an element associated with the
   *    dropzone drag group id to accept drops of the currently dragged element (if it exists)
   *  (and consequently become "Entered" while the dragged element is hovering over it)
   *
   * @param dropzoneDragGroupIds
   *  An array with the drag group ids associated with the dropzone
   *
   */
  const allowedDropDragGroups = useCallback(
    (dropzoneDragGroupIds: DropzoneDragGroupIds): DropzoneDragGroupIds => {
      const activatedDropzoneDragGroupIds =
        getCurrentActivatedDropzoneDragGroupIds()
      return dropzoneDragGroupIds.filter((dropzoneDragGroupIdOrIds) =>
        shouldAcceptDrop(
          dropzoneDragGroupIdOrIds,
          activatedDropzoneDragGroupIds,
        ),
      )
    },
    [getCurrentActivatedDropzoneDragGroupIds],
  )

  /**
   * A utility meant to be used by **DragGroupEffect** to register a callback when
   *  a drag & drop event occurs that is related to the drag group
   *
   * @param dragGroupId
   *
   * the drag group id (or combined collection of ids) for which to register the callback
   *
   * @param dragEffectCallback
   *
   * the callback that will be called when drag & drop events are fired
   */
  const registerCallback = useCallback(
    (
      dragGroupId: string | string[],
      dragEffectCallback: DragEffectCallback,
    ): DragEffectCallback => {
      const filteredDragEffectCallback: DragEffectCallback | undefined =
        Array.isArray(dragGroupId)
          ? (
              event: Event,
              draggedElem: HTMLElement | undefined,
              dropzoneElem: HTMLElement | undefined,
              activeDragGroupIds: string[],
              matchingDragGroupIds?: DropzoneDragGroupIds | undefined,
            ) => {
              if (dragGroupId.some((id) => !activeDragGroupIds.includes(id))) {
                // the drag group was only a partial match, so do not fire the true callback
                return
              }
              dragEffectCallback(
                event,
                draggedElem,
                dropzoneElem,
                activeDragGroupIds,
                matchingDragGroupIds,
              )
            }
          : undefined

      callbackRegistryRef.current = {
        ...callbackRegistryRef.current,
        ...(Array.isArray(dragGroupId)
          ? !filteredDragEffectCallback
            ? {}
            : dragGroupId.reduce((agg, partialDragGroupId) => {
                agg[partialDragGroupId] = (
                  callbackRegistryRef.current[partialDragGroupId] ?? []
                ).concat(filteredDragEffectCallback)
                return agg
              }, {} as Record<string, DragEffectCallback[]>)
          : {
              [dragGroupId]: (
                callbackRegistryRef.current[dragGroupId] ?? []
              ).concat(dragEffectCallback),
            }),
      }
      return filteredDragEffectCallback
        ? filteredDragEffectCallback
        : dragEffectCallback
    },
    [],
  )

  /**
   *  A utility meant to be used by **DragGroupEffect** to deregister a callback when
   *   the callback no longer applies
   *  (e.g. when the **DragGroupEffect**  component unmounts or has new updates)
   *
   * @param dragGroupId
   *
   * the drag group id (or combined collection of ids) for which to register the callback
   *
   * @param dragEffectCallback
   *
   * the callback that will be called when drag & drop events are fired
   */
  const deregisterCallback = useCallback(
    (
      dragGroupId: string | string[],
      dragEffectCallback: DragEffectCallback,
    ) => {
      const updatedCallbackRegistryRef = {
        ...callbackRegistryRef.current,
      }
      const dragGroupIdArr = Array.isArray(dragGroupId)
        ? dragGroupId
        : [dragGroupId]
      dragGroupIdArr.forEach((id) => {
        updatedCallbackRegistryRef[id] = (
          callbackRegistryRef.current[id] ?? []
        ).filter((cb) => cb !== dragEffectCallback)
      })

      callbackRegistryRef.current = updatedCallbackRegistryRef
    },
    [],
  )

  /**
   * The **onEvent** function is responsible for dispatching
   *  all the relevant callbacks that are registered when
   *  any drag & drop event occurs
   */
  const onEvent = useCallback(
    (
      event: Event,
      activeDragGroupIds: string[],
      matchingDragGroupIds?: DropzoneDragGroupIds,
    ): void => {
      const allCallbacks = getCurrentActivatedDropzoneDragGroupIds().flatMap(
        (dragGroupId): DragEffectCallback[] =>
          callbackRegistryRef.current[dragGroupId] ?? [],
      )
      const combinedDragEffectCallback: DragEffectCallback = (...args) => {
        allCallbacks.forEach((callback) => callback(...args))
      }
      combinedDragEffectCallback(
        event,
        activelyDraggedElemRef.current ?? undefined,
        activelyEnteredElemRef.current ?? undefined,
        activeDragGroupIds,
        matchingDragGroupIds,
      )
    },
    [getCurrentActivatedDropzoneDragGroupIds],
  )

  /**
   * The **getDraggableListeners** function constructs the event listener
   *  mapping for events that may be fired if this draggable element
   *  is dragged
   *
   * @param draggableElem
   * The element that may be dragged
   *
   * @param dragGroupIds
   * The drag group ids associated with the element
   */
  const getDraggableListeners = useCallback(
    (dragGroupIds: string[]) => {
      const draggableListeners = {
        dragstart: (event: Event) => {
          if (activelyDraggedElemRef.current) {
            return
          }
          const draggableElem = event.currentTarget as HTMLElement
          if (!isDraggingEnabled(dragGroupIds)) {
            return
          }
          const activeDragGroupIds = dragGroupIds.filter((dragGroupId) =>
            enabledDragGroupIdsRef.current.includes(dragGroupId),
          )
          setActivelyDraggedElem(draggableElem)
          setCurrentlyDraggingDragGroupIds(dragGroupIds)
          onEvent(event, activeDragGroupIds)
        },
        dragend: (event: Event) => {
          const draggableElem = event.currentTarget as HTMLElement
          if (!isDraggingEnabled(dragGroupIds)) {
            return
          }
          const activeDragGroupIds = dragGroupIds.filter((dragGroupId) =>
            enabledDragGroupIdsRef.current.includes(dragGroupId),
          )
          onEvent(event, activeDragGroupIds)
          if (activelyDraggedElemRef.current === draggableElem) {
            setActivelyDraggedElem(null)
            setCurrentlyDraggingDragGroupIds([])
          }
        },
      }
      return draggableListeners
    },
    [
      isDraggingEnabled,
      onEvent,
      setActivelyDraggedElem,
      setCurrentlyDraggingDragGroupIds,
    ],
  )

  /**
   * The **getDropzoneListeners** function constructs the event listener
   *  mapping for events that may be fired on the dropzone element
   *  if a dragged element interacts with it
   *
   * @param dropzoneElem
   * The element that may be dragged onto as a dropzone
   *
   * @param dropzoneDragGroupIds
   *  An array with the drag group ids associated with the dropzone
   */
  const getDropzoneListeners = useCallback(
    (dropzoneDragGroupIds: DropzoneDragGroupIds) => {
      const dropzoneListeners = {
        dragenter: (event: Event) => {
          const dropzoneElem = event.currentTarget as HTMLElement
          const matchingDropzoneDragGroupIds =
            allowedDropDragGroups(dropzoneDragGroupIds)
          if (matchingDropzoneDragGroupIds.length) {
            event.preventDefault()
            setActivelyEnteredElem(dropzoneElem)
            onEvent(
              event,
              getCurrentActivatedDropzoneDragGroupIds(),
              matchingDropzoneDragGroupIds,
            )
          }
        },
        dragleave: (event: Event) => {
          const dropzoneElem = event.currentTarget as HTMLElement
          const matchingDropzoneDragGroupIds =
            allowedDropDragGroups(dropzoneDragGroupIds)
          if (matchingDropzoneDragGroupIds.length) {
            onEvent(
              event,
              getCurrentActivatedDropzoneDragGroupIds(),
              matchingDropzoneDragGroupIds,
            )
          }
          if (activelyEnteredElemRef.current === dropzoneElem) {
            setActivelyEnteredElem(null)
          }
        },
        dragover: (event: Event) => {
          const dropzoneElem = event.currentTarget as HTMLElement
          const matchingDropzoneDragGroupIds =
            allowedDropDragGroups(dropzoneDragGroupIds)
          if (matchingDropzoneDragGroupIds.length) {
            event.preventDefault() // makes it a dropzone
            setActivelyEnteredElem(dropzoneElem)
            onEvent(
              event,
              getCurrentActivatedDropzoneDragGroupIds(),
              matchingDropzoneDragGroupIds,
            )
          }
        },
        drop: (event: Event) => {
          const dropzoneElem = event.currentTarget as HTMLElement
          const matchingDropzoneDragGroupIds =
            allowedDropDragGroups(dropzoneDragGroupIds)
          if (matchingDropzoneDragGroupIds.length) {
            onEvent(
              event,
              getCurrentActivatedDropzoneDragGroupIds(),
              matchingDropzoneDragGroupIds,
            )
          }
          if (activelyEnteredElemRef.current === dropzoneElem) {
            setActivelyEnteredElem(null)
          }
        },
      }
      return dropzoneListeners
    },
    [
      allowedDropDragGroups,
      onEvent,
      setActivelyEnteredElem,
      getCurrentActivatedDropzoneDragGroupIds,
    ],
  )

  /**
   * The **getListenerMapping** function constructs the event listener
   *  mapping for all events that may be fired on the element
   *  if it is dragged or interacts as a dropzone with a dragged
   *  element
   *
   * @param dragGroupIds
   * The drag group ids associated with the element
   *  in the capacity as a draggable element
   *
   * @param dropzoneDragGroupIds
   *  An array with the drag group ids associated with the dropzone
   *
   * @returns an object mapping drag & drop listener event types
   *  to event listener callbacks
   */
  const getListenerMapping = useCallback(
    (
      dragGroupIds: string[],
      dropzoneDragGroupIds: DropzoneDragGroupIds,
    ): DragAndDropListenerMapping => {
      if (!dragGroupIds.length && !dropzoneDragGroupIds.length) {
        return {}
      }
      return {
        ...(dragGroupIds.length ? getDraggableListeners(dragGroupIds) : {}),
        ...(dropzoneDragGroupIds.length
          ? getDropzoneListeners(dropzoneDragGroupIds)
          : {}),
      }
    },
    [getDraggableListeners, getDropzoneListeners],
  )

  const value: DragNDropContextValue = useMemo(
    () => ({
      // disabled/enabled
      enabledDragGroupIds,
      isDraggingEnabled,
      toggleDragDrops,
      // active dragging/entering
      currentlyDraggingDragGroupIds,
      activelyDraggedElem,
      activelyDraggedElemRef,
      activelyEnteredElem,
      activelyEnteredElemRef,
      // side-effect registration
      registerCallback,
      deregisterCallback,
      getListenerMapping,
    }),
    [
      activelyDraggedElem,
      activelyEnteredElem,
      currentlyDraggingDragGroupIds,
      deregisterCallback,
      enabledDragGroupIds,
      getListenerMapping,
      isDraggingEnabled,
      registerCallback,
      toggleDragDrops,
    ],
  )
  return (
    <DragNDropStateContext.Provider value={value}>
      {children}
    </DragNDropStateContext.Provider>
  )
}

export default DragNDropProvider
